import DrawerCloseIcon from "@mixitone/icons/drawer-close.svg";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { type PropsWithChildren, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { Spinner } from "./Spinner";
import React from "react";

function isTestMode(): boolean {
  // @ts-ignore
  return window.__TEST_MODE;
}

interface DrawerProps {
  spinner?: boolean;
  children?: React.ReactNode;
  onClose: () => void;
  className?: string;
  /** Secondary drawers stack above primary (`z-[60]` vs `z-50`). */
  layer?: "primary" | "secondary";
}
interface State {
  spinner: boolean;
  state: "opening" | "open" | "closing" | "closed";
}

export class DrawerController extends ApplicationController<State, DrawerProps> {
  onClose?: () => void;

  get initialState(): State {
    return {
      spinner: false,
      state: "closed",
    };
  }

  async changeProps(newProps: DrawerProps) {
    this.onClose = newProps.onClose;
  }

  actionClose() {
    this.state.state = "closing";
    // Just in case the closing animation is not triggered
    setTimeout(() => {
      this.actionTransitionEnd();
    }, 500);
  }

  actionSetMounted() {
    this.state.state = "opening";
  }

  actionDoneOpening() {
    this.state.state = "open";
  }

  actionDoneClosing() {
    this.state.state = "closed";
    this.onClose?.();
  }

  actionTransitionEnd() {
    if (this.state.state === "opening") {
      this.actionDoneOpening();
    } else if (this.state.state === "closing") {
      this.actionDoneClosing();
    }
  }
}

const ControlledDrawer: React.FC<DrawerProps> = ({ children, spinner, className, layer = "primary" }) => {
  const controller = DrawerController.use();
  const { state } = controller.state;

  const drawerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle initial mount animation
  useEffect(() => {
    requestAnimationFrame(() => {
      controller.actionSetMounted();
    });
  }, []);

  // useOutsideAlerter(
  //   drawerRef,
  //   () => {
  //     controller.actionClose();
  //   },
  //   { skipElements: ["[role=menu]", "[role=option]", "[role=dialog]"], active: state === "open" },
  //   [state],
  // );

  const handleBackgroundClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      controller.actionClose();
    },
    [controller],
  );

  const handleTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      // Only handle transform transitions
      if (e.propertyName === "transform" || e.propertyName === "translate") {
        controller.actionTransitionEnd();
      }
    },
    [controller],
  );

  const id = useId();
  const [drawerContainer, _setDrawerContainer] = useState(() => {
    const container = document.createElement("div");
    container.id = id;
    return container;
  });

  useEffect(() => {
    document.body.appendChild(drawerContainer);

    return () => {
      if (document.body.contains(drawerContainer)) {
        document.body.removeChild(drawerContainer);
      }
    };
  }, [drawerContainer]);

  // Mobile browsers can scroll the page behind fixed overlays (e.g. after focusing a search
  // field). Snap the window back when the drawer closes so headers stay in view.
  useEffect(() => {
    return () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document.getElementById("root")?.scrollTo(0, 0);
    };
  }, []);

  const drawer = (
    <div
      className={clsx("fixed inset-0 bg-black/10", layer === "secondary" ? "z-[60]" : "z-50")}
      style={{ right: "var(--mixitone-assistant-width, 0px)" }}
      onClick={handleBackgroundClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
        className={clsx(
          ["fixed", "top-0", "h-screen", "w-full sm:w-[500px]", "transition-transform", "ease-in-out"],
          className,
          {
            "translate-x-full": state === "closed" || state === "closing",
            "duration-300": !isTestMode(),
            "duration-0": isTestMode(),
          },
        )}
        style={{ right: "var(--mixitone-assistant-width, 0px)" }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div
          className={clsx([
            "drawer",
            "w-full",
            "h-full",
            "overflow-y-auto",
            "border-l",
            "border-t",
            "rounded-tl-lg",
            "border-slate-300",
            "bg-slate-100",
            "shadow-xl shadow-gray-400",
            "p-4",
          ])}
          tabIndex={-1}
          aria-labelledby="drawer-right-label"
          aria-roledescription="drawer"
          aria-hidden={state === "closed"}
          ref={drawerRef}
        >
          {spinner && <DrawerSpinner />}
          {!spinner && children}
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, drawerContainer);
};

const DefaultIcon = () => (
  <svg
    className="mr-2.5 h-4 w-4"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
  </svg>
);

const DrawerTitleComponent: React.FC<
  React.PropsWithChildren<{
    icon?: React.ReactElement | React.ComponentType<{ className: string | undefined }>;
    contextLink?: React.ReactNode;
  }>
> = ({ icon, children, contextLink }) => {
  const controller = DrawerController.use();

  let iconElement: React.ReactNode = useMemo(() => {
    if (icon) {
      if (React.isValidElement(icon)) {
        return icon;
      } else {
        return React.createElement(icon as React.ComponentType<{ className: string | undefined }>, {
          className: "mr-2 w-[36px] stroke-gray-500 fill-gray-500 text-gray-500",
        });
      }
    } else {
      return <DefaultIcon />;
    }
  }, [icon]);

  return (
    <div
      className="flex items-center justify-between mb-4 text-base font-semibold text-gray-800"
      // biome-ignore lint/a11y/useAriaPropsForRole: meh
      // biome-ignore lint/a11y/useSemanticElements: meh
      role="heading"
    >
      <div className="flex items-center">
        {iconElement}
        {children}
      </div>
      <div className="flex items-center gap-2">
        {contextLink}
        <button
          onClick={() => controller.actionClose()}
          className="p-1 rounded-full hover:bg-gray-200"
          aria-label="Close drawer"
        >
          <DrawerCloseIcon className="h-[20px] w-[20px] text-gray-500" />
        </button>
      </div>
    </div>
  );
};
/**
 * The top title of the drawer plus the close button
 */
const DrawerTitle = ApplicationView(DrawerTitleComponent);
export { DrawerTitle };

const DrawerSpinnerComponent: React.FC = () => {
  return (
    <div className="flex items-center justify-center w-full h-full">
      <Spinner size={64} />
    </div>
  );
};
const DrawerSpinner = ApplicationView(DrawerSpinnerComponent);
export { DrawerSpinner };

const Drawer = DrawerController.scope(ApplicationView(ControlledDrawer));
export { Drawer };

export const DrawerCard: React.FC<
  PropsWithChildren<{
    className?: string;
    title?: React.ReactNode;
  }>
> = ({ className, children, title }) => {
  return (
    <div className={twMerge("flex w-full flex-col gap-5 rounded bg-white p-4", className)}>
      {title && <h2 className="text-lg font-medium text-gray-800">{title}</h2>}
      {children}
    </div>
  );
};

export const DrawerField = ApplicationView(
  ({
    label,
    helpText,
    className,
    children,
    autoFocus,
    htmlFor,
    labelWidth = "normal",
  }: {
    label: React.ReactNode;
    helpText?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
    autoFocus?: boolean;
    htmlFor?: string;
    labelWidth?: "normal" | "wide";
  }) => {
    return (
      <div className={twMerge("drawer-field flex flex-col", className)}>
        <div className="flex items-center w-full">
          <label
            className={clsx("text-gray-600", {
              "w-1/3": labelWidth !== "wide",
              "w-2/3": labelWidth === "wide",
            })}
            autoFocus={autoFocus}
            htmlFor={htmlFor}
          >
            {label}
          </label>
          <div
            className={clsx("flex items-center gap-2 pl-2 *:w-full", {
              "w-1/3": labelWidth === "wide",
              "w-full": labelWidth !== "wide",
            })}
          >
            {children}
          </div>
        </div>
        {helpText && (
          <div className="flex w-full mt-1">
            <div className="w-1/3" />
            <p className="w-full pl-2 text-xs text-gray-500">{helpText}</p>
          </div>
        )}
      </div>
    );
  },
);
